<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex align-center mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold">Products</h1>
        <p class="text-body-2 grey--text mt-1">Manage device types and import existing serials</p>
      </div>
      <v-spacer />
      <v-btn small rounded color="primary" outlined @click="showImportDialog = true" class="text-none">
        <v-icon left small>mdi-content-paste</v-icon>
        Import Serials
      </v-btn>
    </div>

    <!-- Add Product + List -->
    <v-card class="elevation-0 mb-6">
      <v-card-text class="pa-6">
        <div class="text-caption text-uppercase font-weight-bold grey--text mb-3">Add New Product</div>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="newProductCode"
              placeholder="Product code (e.g. ER10C)"
              outlined
              dense
              hide-details
              rounded
              maxlength="10"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              rounded
              block
              @click="addProduct"
              :loading="adding"
              :disabled="!newProductCode"
              class="text-none"
            >
              <v-icon left small>mdi-plus</v-icon>
              Add Product
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- Product Cards -->
      <v-card-text class="pa-6" v-if="productDetails.length">
        <v-row>
          <v-col v-for="prod in productDetails" :key="prod.code" cols="12" sm="6" md="4">
            <div class="product-card pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="text-subtitle-1 font-weight-bold monospace">{{ prod.code }}</span>
                <v-spacer />
                <v-btn icon x-small @click="confirmDelete(prod.code)">
                  <v-icon small color="grey">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
              <div class="d-flex">
                <div class="stat-block mr-6">
                  <div class="text-h6 font-weight-bold success--text">{{ prod.usedCount }}</div>
                  <div class="text-caption grey--text">Used</div>
                </div>
                <div class="stat-block">
                  <div class="text-h6 font-weight-bold primary--text">{{ prod.unusedCount }}</div>
                  <div class="text-caption grey--text">Available</div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else class="text-center pa-12">
        <v-icon size="48" color="grey lighten-1">mdi-package-variant-closed</v-icon>
        <div class="text-body-2 grey--text mt-3">No products registered yet</div>
      </v-card-text>
    </v-card>

    <!-- Import History -->
    <v-card class="elevation-0" v-if="importLogs.length">
      <v-card-text class="pa-6 pb-2">
        <div class="text-caption text-uppercase font-weight-bold grey--text">Import History</div>
      </v-card-text>
      <v-data-table
        :headers="importHeaders"
        :items="importLogs"
        :items-per-page="5"
        dense
        :loading="loadingLogs"
        class="px-2"
      >
        <template v-slot:item.imported_at="{ item }">
          {{ item.imported_at ? new Date(item.imported_at).toLocaleString() : '' }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="elevation-0">
        <v-card-title class="text-subtitle-1 font-weight-bold">Delete Product</v-card-title>
        <v-card-text>
          Delete <strong class="monospace">{{ deleteTarget }}</strong> and all its serials? This cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text rounded small @click="deleteDialog = false" class="text-none">Cancel</v-btn>
          <v-btn color="error" rounded small @click="deleteProduct" :loading="deleting" class="text-none">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import Dialog -->
    <v-dialog v-model="showImportDialog" max-width="560">
      <v-card class="elevation-0">
        <v-card-title class="text-subtitle-1 font-weight-bold">Import Serials</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text mb-4">
            Paste serial numbers below (one per line). You can copy a column from Excel.
            Imported serials will be marked as used to prevent duplicates.
          </p>
          <v-textarea
            v-model="importText"
            placeholder="ER10C-1ZFFA-3E&#10;ER10C-24H1M-38&#10;ER10M-ABCDE-FG"
            outlined
            rounded
            rows="8"
            :disabled="importing"
            class="monospace"
            hide-details
          />
          <v-alert v-if="importResult" type="success" dense rounded="lg" class="mt-4">
            Imported {{ importResult.imported }} serials
            <span v-if="importResult.skipped"> ({{ importResult.skipped }} already existed)</span>
          </v-alert>
          <v-alert v-if="importResult && importResult.invalid && importResult.invalid.length" type="warning" dense rounded="lg" class="mt-3">
            {{ importResult.invalid.length }} invalid:
            <span class="monospace">{{ importResult.invalid.join(', ') }}</span>
          </v-alert>
          <v-alert v-if="importError" type="error" dense rounded="lg" class="mt-3">{{ importError }}</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text rounded small @click="showImportDialog = false" class="text-none">Close</v-btn>
          <v-btn color="primary" rounded small @click="runImport" :loading="importing" :disabled="!importText" class="text-none">
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top rounded="pill">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '../api';

export default {
  name: 'ProductManager',
  data: function () {
    return {
      newProductCode: '',
      adding: false,
      productDetails: [],
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      showImportDialog: false,
      importing: false,
      importText: '',
      importResult: null,
      importError: null,
      importLogs: [],
      loadingLogs: false,
      snackbar: { show: false, text: '', color: 'success' },
      importHeaders: [
        { text: 'Date', value: 'imported_at' },
        { text: 'Product', value: 'product_code' },
        { text: 'Count', value: 'count' },
        { text: 'By', value: 'imported_by' },
      ],
    };
  },
  mounted: function () {
    this.loadProducts();
    this.loadImportLogs();
  },
  methods: {
    loadProducts: function () {
      var self = this;
      var products = this.$store.state.products;
      var details = [];
      var promises = products.map(function (code) {
        return Promise.all([
          api.get('/serial/' + code + '/used'),
          api.get('/serial/' + code + '/unused'),
        ])
          .then(function (results) {
            details.push({
              code: code,
              usedCount: results[0].data.length,
              unusedCount: results[1].data.length,
            });
          })
          .catch(function () {
            details.push({ code: code, usedCount: '?', unusedCount: '?' });
          });
      });
      Promise.all(promises).then(function () {
        self.productDetails = details;
      });
    },
    addProduct: function () {
      if (!this.newProductCode) return;
      var self = this;
      self.adding = true;
      api
        .post('/products', { product_code: self.newProductCode.toUpperCase() })
        .then(function () {
          self.newProductCode = '';
          return self.$store.dispatch('fetchProducts');
        })
        .then(function () {
          self.loadProducts();
          self.notify('Product added', 'success');
        })
        .catch(function (err) {
          self.notify(
            (err.response && err.response.data && err.response.data.detail) || 'Failed',
            'error',
          );
        })
        .finally(function () {
          self.adding = false;
        });
    },
    confirmDelete: function (code) {
      this.deleteTarget = code;
      this.deleteDialog = true;
    },
    deleteProduct: function () {
      var self = this;
      self.deleting = true;
      api
        .delete('/products/' + self.deleteTarget)
        .then(function () {
          self.deleteDialog = false;
          return self.$store.dispatch('fetchProducts');
        })
        .then(function () {
          self.loadProducts();
          self.notify('Product deleted', 'success');
        })
        .catch(function (err) {
          self.notify(
            (err.response && err.response.data && err.response.data.detail) || 'Failed',
            'error',
          );
        })
        .finally(function () {
          self.deleting = false;
        });
    },
    runImport: function () {
      var self = this;
      self.importing = true;
      self.importResult = null;
      self.importError = null;
      api
        .post('/migration/import', { serials_text: self.importText })
        .then(function (res) {
          self.importResult = res.data;
          self.importText = '';
          return self.$store.dispatch('fetchProducts');
        })
        .then(function () {
          self.loadProducts();
          self.loadImportLogs();
        })
        .catch(function (err) {
          self.importError =
            (err.response && err.response.data && err.response.data.detail) || 'Import failed';
        })
        .finally(function () {
          self.importing = false;
        });
    },
    loadImportLogs: function () {
      var self = this;
      self.loadingLogs = true;
      api
        .get('/migration/status')
        .then(function (res) {
          self.importLogs = res.data.logs;
        })
        .catch(function () {})
        .finally(function () {
          self.loadingLogs = false;
        });
    },
    notify: function (text, color) {
      this.snackbar = { show: true, text: text, color: color };
    },
  },
};
</script>

<style scoped>
.monospace { font-family: 'Roboto Mono', monospace; }
.product-card {
  border-radius: 12px;
  border: 1px solid rgba(108, 99, 255, 0.15);
  background: rgba(108, 99, 255, 0.04);
}
.product-card:hover {
  border-color: rgba(108, 99, 255, 0.3);
  background: rgba(108, 99, 255, 0.08);
}
</style>
